import { Home, HeartPulse, HandHeart, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const PC_IMG =
  "https://images.unsplash.com/photo-1781344334963-264f725d668b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZSUyMGV4dGVyaW9yJTIwZHVzayUyMGRyaXZld2F5fGVufDB8fHx8MTc5MDg3NTA1MHww&ixlib=rb-4.1.0&q=85";
const MEDICAID_IMG =
  "https://images.unsplash.com/photo-1609260840244-22b52bfc6664?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxibGFjayUyMGZhbWlseSUyMG11bHRpJTIwZ2VuZXJhdGlvbmFsJTIwd2FybSUyMGNhcmluZyUyMHNlbmlvcnxlbnwwfHx8MTc5MDg3NTA1MHww&ixlib=rb-4.1.0&q=85";

const STATS = [
  { value: "3", label: "Coverage lines under one roof" },
  { value: "100%", label: "Independent — we work for you, not a carrier" },
  { value: "404", label: "Homegrown Atlanta expertise" },
];

const Card = ({ testId, icon: Icon, title, desc, items, image, tall, accent }) => (
  <div
    data-testid={testId}
    className={`spotlight-card group relative flex flex-col overflow-hidden rounded-3xl ${
      tall ? "col-span-12 lg:col-span-7 lg:row-span-2" : "col-span-12 sm:col-span-6 lg:col-span-5"
    }`}
  >
    {image && (
      <div className="relative h-56 overflow-hidden lg:h-64">
        <img
          src={image}
          alt={title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--ns-bg-2)] via-transparent to-transparent" />
      </div>
    )}
    <div className="flex flex-1 flex-col p-7 sm:p-9">
      <div className={`mb-5 inline-flex h-11 w-11 items-center justify-center rounded-xl border ${accent}`}>
        <Icon size={20} />
      </div>
      <h3 className="font-serif text-2xl tracking-tight text-[color:var(--ns-ink)] sm:text-3xl">{title}</h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[color:var(--ns-muted)] sm:text-base">{desc}</p>
      <ul className="mt-6 space-y-2.5">
        {items.map((item) => (
          <li key={item} className="flex items-center gap-3 text-sm text-[color:var(--ns-ink)]/90">
            <span className="h-1 w-4 rounded-full bg-[color:var(--ns-gold)]/70" />
            {item}
          </li>
        ))}
      </ul>
      <a
        href="#quote"
        data-testid={`${testId}-cta`}
        className="mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--ns-gold)] transition-colors hover:text-[color:var(--ns-amber)]"
      >
        Discuss this coverage
        <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </div>
  </div>
);

export const Services = () => (
  <section id="coverage" data-testid="services-section" className="relative py-24 sm:py-32 lg:py-36">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
      <Reveal>
        <Eyebrow testId="services-eyebrow">What we protect</Eyebrow>
        <h2
          data-testid="services-heading"
          className="max-w-2xl font-serif text-2xl tracking-tight text-[color:var(--ns-ink)] sm:text-3xl lg:text-4xl"
        >
          Three lines of coverage. <em className="text-[color:var(--ns-gold)]">One guiding star.</em>
        </h2>
      </Reveal>

      <div className="mt-14 grid grid-cols-12 gap-5">
        <Reveal className="col-span-12 lg:col-span-7 lg:row-span-2" delay={0.05}>
          <Card
            testId="service-pc-card"
            icon={Home}
            title="Home & Auto"
            desc="Your home and vehicles are the foundation. We shop multiple carriers to protect them properly — without the fine-print surprises."
            items={["Homeowners & Renters", "Auto & Motorcycle", "Landlord & Investment Property", "Umbrella Coverage"]}
            image={PC_IMG}
            tall
            accent="border-blue-500/25 bg-blue-500/10 text-blue-400"
          />
        </Reveal>
        <Reveal className="col-span-12 sm:col-span-6 lg:col-span-5" delay={0.12}>
          <Card
            testId="service-life-card"
            icon={HeartPulse}
            title="Life & Health"
            desc="Term and whole life, individual health plans, and supplemental coverage — explained in plain language, sized to your family."
            items={["Term & Whole Life", "Individual & Family Health", "Supplemental & Final Expense"]}
            accent="border-emerald-500/25 bg-emerald-500/10 text-emerald-400"
          />
        </Reveal>
        <Reveal className="col-span-12 sm:col-span-6 lg:col-span-5" delay={0.18}>
          <Card
            testId="service-medicaid-card"
            icon={HandHeart}
            title="Medicaid Guidance"
            desc="Eligibility, enrollment, and renewals — patient, step-by-step help navigating Georgia Medicaid for you or a loved one."
            items={["Eligibility Review", "Application & Enrollment Help", "Renewal Support"]}
            image={MEDICAID_IMG}
            accent="border-amber-500/25 bg-amber-500/10 text-amber-400"
          />
        </Reveal>
      </div>

      <div className="mt-5 grid grid-cols-12 gap-5">
        {STATS.map((s, i) => (
          <Reveal key={s.label} className="col-span-12 sm:col-span-4" delay={0.08 * i}>
            <div data-testid={`stat-card-${i}`} className="spotlight-card rounded-3xl p-7">
              <p className="font-serif text-4xl text-[color:var(--ns-gold)] sm:text-5xl">{s.value}</p>
              <p className="mt-2 text-sm text-[color:var(--ns-muted)]">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
